import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Lock, Mail, Phone, User } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { SocialLoginButtons } from '@/components/auth/SocialLoginButtons'
import { useRegister } from '@/hooks/useAuth'
import { getFieldErrors } from '@/lib/api'
import {
  DEFAULT_PHONE_COUNTRY_CODE,
  PHONE_COUNTRY_CODE_OPTIONS,
} from '@/lib/phoneCountryCodes'

const schema = z
  .object({
    name: z.string().min(2, 'Name must be at least 2 characters'),
    email: z.string().email('Enter a valid email address'),
    phone_country_code: z.string().min(2, 'Select a country code'),
    phone: z
      .string()
      .regex(/^[0-9]{6,15}$/, 'Enter a valid mobile number (digits only)'),
    password: z.string().min(8, 'Password must be at least 8 characters'),
    password_confirmation: z.string(),
  })
  .refine((d) => d.password === d.password_confirmation, {
    message: 'Passwords do not match',
    path: ['password_confirmation'],
  })

type FormData = z.infer<typeof schema>

const countryCodeOptions = PHONE_COUNTRY_CODE_OPTIONS.map((o) => ({
  value: o.value,
  label: o.label,
}))

export function RegisterForm({ backgroundLocation }: { backgroundLocation?: unknown }) {
  const { t } = useTranslation('auth')
  const navigate = useNavigate()
  const registerMutation = useRegister()

  const {
    register: field,
    control,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      phone_country_code: DEFAULT_PHONE_COUNTRY_CODE,
    },
  })

  async function onSubmit(values: FormData) {
    try {
      await registerMutation.mutateAsync(values)
      navigate('/auth/check-email', {
        replace: true,
        state: { email: values.email, context: 'register' },
      })
    } catch (err) {
      const fieldErrors = getFieldErrors(err)
      Object.entries(fieldErrors).forEach(([f, msg]) => {
        setError(f as keyof FormData, { message: msg })
      })
    }
  }

  return (
    <div className="space-y-6">
      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
        <Input
          label={t('name')}
          type="text"
          autoComplete="name"
          leftIcon={<User className="h-4 w-4" />}
          error={errors.name?.message}
          {...field('name')}
        />
        <Input
          label={t('email')}
          type="email"
          autoComplete="email"
          leftIcon={<Mail className="h-4 w-4" />}
          error={errors.email?.message}
          {...field('email')}
        />

        <div className="space-y-1">
          <span className="block text-sm font-medium text-gray-700">{t('mobile_number')}</span>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,9rem)_1fr]">
            <Controller
              name="phone_country_code"
              control={control}
              render={({ field: countryField }) => (
                <Select
                  options={countryCodeOptions}
                  value={countryField.value}
                  onValueChange={countryField.onChange}
                  placeholder={t('country_code')}
                  error={errors.phone_country_code?.message}
                  aria-label={t('country_code')}
                />
              )}
            />
            <Input
              type="tel"
              autoComplete="tel-national"
              inputMode="numeric"
              placeholder={t('mobile_number_placeholder')}
              leftIcon={<Phone className="h-4 w-4" />}
              error={errors.phone?.message}
              {...field('phone', {
                setValueAs: (value: string) => value.replace(/\D/g, ''),
              })}
            />
          </div>
        </div>

        <Input
          label={t('password')}
          type="password"
          autoComplete="new-password"
          leftIcon={<Lock className="h-4 w-4" />}
          error={errors.password?.message}
          hint={t('password_min')}
          {...field('password')}
        />
        <Input
          label={t('confirm_password')}
          type="password"
          autoComplete="new-password"
          leftIcon={<Lock className="h-4 w-4" />}
          error={errors.password_confirmation?.message}
          {...field('password_confirmation')}
        />

        <Button type="submit" className="w-full" loading={registerMutation.isPending}>
          {t('register')}
        </Button>
      </form>

      <SocialLoginButtons />

      <p className="text-center text-sm text-gray-600">
        {t('have_account')}{' '}
        <Link
          to="/auth/login"
          state={backgroundLocation ? { backgroundLocation } : undefined}
          className="font-medium text-primary-600 hover:underline"
        >
          {t('sign_in')}
        </Link>
      </p>
    </div>
  )
}
